import { createFileRoute } from "@tanstack/react-router";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  completeImportBatch,
  importExternalBookings,
  listExternalBookings,
  listImportTemplates,
  removeExternalBooking,
  saveExternalBooking,
  saveImportTemplate,
  startImportBatch,
} from "@/lib/external-bookings.functions";
import { Link } from "@tanstack/react-router";
import { listProviderDirectory } from "@/lib/provider-payments.functions";
import {
  BOOKING_STATUSES,
  BOOKING_STATUS_LABEL,
  CHANNELS,
  CHANNEL_LABEL,
  CURRENCIES,
  IMPORT_FIELDS,
  PAYMENT_STATUSES,
  PAYMENT_STATUS_LABEL,
  bookingFilters,
  money,
  suggestMapping,
  type BookingFilters,
  type ExternalBookingInput,
  IMPORT_SOURCES,
  IMPORT_FIELD_LABEL,
  mappingReport,
} from "@/lib/provider-schemas";
import { parseTabularFile } from "@/lib/import-parse";
import { downloadCsv } from "@/lib/csv-export";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/admin/external-bookings")({
  head: () => ({
    meta: [
      { title: "Reservas externas — Administración" },
      {
        name: "description",
        content: "Reservas de ventas externas: costos de proveedor, pagos y conciliación.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Reservas externas"
      description="Reservas provenientes de OTAs y ventas externas, con costo de proveedor, estado de pago y utilidad por reserva."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

const providersQuery = queryOptions({
  queryKey: ["admin", "provider-directory"],
  queryFn: () => listProviderDirectory(),
});

const defaultFilters = bookingFilters.parse({});

function Body() {
  const [filters, setFilters] = useState<BookingFilters>(defaultFilters);
  const qc = useQueryClient();
  const { data: dir } = useSuspenseQuery(providersQuery);
  const { data } = useSuspenseQuery(
    queryOptions({
      queryKey: ["admin", "external-bookings", filters],
      queryFn: () => listExternalBookings({ data: filters }),
    }),
  );
  const [editing, setEditing] = useState<ExternalBookingInput | null>(null);
  const [showImport, setShowImport] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "external-bookings"] });
    qc.invalidateQueries({ queryKey: ["admin", "provider-directory"] });
    qc.invalidateQueries({ queryKey: ["admin", "provider-dashboard"] });
  };

  const save = useMutation({
    mutationFn: (input: ExternalBookingInput) => saveExternalBooking({ data: input }),
    onSuccess: () => {
      toast.success("Reserva guardada");
      setEditing(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id: string) => removeExternalBooking({ data: { id } }),
    onSuccess: () => {
      toast.success("Reserva eliminada");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const providers = dir.providers;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Reservas" value={data.totals.bookings} hint={`${data.totals.pax} pasajeros`} />
        <StatCard label="Con diferencia" value={data.totals.differences} />
        <StatCard
          label="Canceladas"
          value={data.totals.cancelled.count}
          hint={`No show: ${data.totals.noShow.count}`}
        />
        <StatCard
          label="Pendiente por pagar"
          value={
            <span className="text-base">
              {data.totals.byCurrency.map((c) => (
                <div key={c.currency}>{money(c.pending, c.currency)}</div>
              ))}
            </span>
          }
        />
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Desde">
            <Input
              type="date"
              value={filters.dateFrom ?? ""}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value || null })}
            />
          </Field>
          <Field label="Hasta">
            <Input
              type="date"
              value={filters.dateTo ?? ""}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value || null })}
            />
          </Field>
          <Field label="Proveedor">
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={filters.providerId ?? ""}
              onChange={(e) => setFilters({ ...filters, providerId: e.target.value || null })}
            >
              <option value="">Todos</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Canal">
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={filters.channel ?? ""}
              onChange={(e) => setFilters({ ...filters, channel: e.target.value || null })}
            >
              <option value="">Todos</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABEL[c]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado de reserva">
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={filters.bookingStatus ?? ""}
              onChange={(e) =>
                setFilters({ ...filters, bookingStatus: (e.target.value || null) as BookingFilters["bookingStatus"] })
              }
            >
              <option value="">Todos</option>
              {BOOKING_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {BOOKING_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado de pago">
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={filters.paymentStatus ?? ""}
              onChange={(e) =>
                setFilters({ ...filters, paymentStatus: (e.target.value || null) as BookingFilters["paymentStatus"] })
              }
            >
              <option value="">Todos</option>
              {PAYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {PAYMENT_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tour">
            <Input
              value={filters.tour ?? ""}
              onChange={(e) => setFilters({ ...filters, tour: e.target.value || null })}
            />
          </Field>
          <Field label="Buscar (cliente, cupón, ID)">
            <Input
              value={filters.search ?? ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value || null })}
            />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setFilters(defaultFilters)}>
            Limpiar filtros
          </Button>
          <Button onClick={() => setShowImport(true)}>Importar Excel / CSV</Button>
          <Button
            variant="outline"
            onClick={() =>
              setEditing({
                ...(bookingFiltersDefaultsBooking as ExternalBookingInput),
              })
            }
          >
            Registrar reserva manual
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              downloadCsv(
                "reservas-externas",
                [
                  { key: "service_date", label: "Fecha" },
                  { key: "channel", label: "Canal" },
                  { key: "external_booking_id", label: "ID externo" },
                  { key: "tour_title", label: "Tour" },
                  { key: "customer_first_name", label: "Nombre" },
                  { key: "customer_last_name", label: "Apellido" },
                  { key: "pax_total", label: "Pax" },
                  { key: "sale_total", label: "Venta" },
                  { key: "sale_currency", label: "Moneda venta" },
                  { key: "ota_commission", label: "Comisión" },
                  { key: "net_revenue", label: "Ingreso neto" },
                  { key: "provider_name", label: "Proveedor" },
                  { key: "provider_cost", label: "Costo" },
                  { key: "cost_currency", label: "Moneda costo" },
                  { key: "paid_amount", label: "Pagado" },
                  { key: "pending_amount", label: "Pendiente" },
                  { key: "profit", label: "Utilidad" },
                  { key: "booking_status", label: "Estado" },
                  { key: "payment_status", label: "Pago" },
                ],
                data.bookings as never,
              )
            }
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Reserva</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Tour</th>
              <th className="px-3 py-2 text-right">Pax</th>
              <th className="px-3 py-2 text-right">Venta</th>
              <th className="px-3 py-2">Proveedor</th>
              <th className="px-3 py-2 text-right">Costo</th>
              <th className="px-3 py-2 text-right">Pagado</th>
              <th className="px-3 py-2 text-right">Pendiente</th>
              <th className="px-3 py-2 text-right">Utilidad</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.bookings.map((b: any) => (
              <tr key={b.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{b.service_date ?? "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{CHANNEL_LABEL[b.channel] ?? b.channel}</div>
                  <div className="text-muted-foreground">{b.external_booking_id ?? b.external_reference}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div>
                    {b.customer_first_name} {b.customer_last_name}
                  </div>
                  <div className="text-muted-foreground">{b.hotel}</div>
                </td>
                <td className="px-3 py-2 text-xs">{b.tour_title}</td>
                <td className="px-3 py-2 text-right tabular-nums">{b.pax_total}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {money(b.sale_total, b.sale_currency)}
                </td>
                <td className="px-3 py-2 text-xs">{b.provider_name ?? "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {money(b.provider_cost, b.cost_currency)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(b.paid_amount, b.cost_currency)}</td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${Number(b.pending_amount) > 0 ? "font-semibold text-destructive" : ""}`}
                >
                  {money(b.pending_amount, b.cost_currency)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(b.profit, b.cost_currency)}</td>
                <td className="px-3 py-2 text-xs">
                  <Badge variant={b.booking_status === "cancelada" ? "destructive" : "secondary"}>
                    {BOOKING_STATUS_LABEL[b.booking_status as keyof typeof BOOKING_STATUS_LABEL] ??
                      b.booking_status}
                  </Badge>
                  <div className="mt-1 text-muted-foreground">
                    {PAYMENT_STATUS_LABEL[b.payment_status as keyof typeof PAYMENT_STATUS_LABEL] ??
                      b.payment_status}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing(toForm(b))}>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm("¿Eliminar esta reserva externa?")) del.mutate(b.id);
                      }}
                    >
                      Borrar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!data.bookings.length ? (
              <tr>
                <td colSpan={13} className="px-3 py-10 text-center text-muted-foreground">
                  Sin reservas con estos filtros.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <BookingForm
        value={editing}
        providers={providers}
        onClose={() => setEditing(null)}
        onSave={(v) => save.mutate(v)}
        saving={save.isPending}
      />

      <ImportDialog
        open={showImport}
        providers={providers}
        onClose={() => setShowImport(false)}
        onDone={invalidate}
      />
    </div>
  );
}

const bookingFiltersDefaultsBooking = {
  channel: "manual",
  pax_adt: 1,
  pax_chd: 0,
  pax_inf: 0,
  pax_senior: 0,
  pax_student: 0,
  pax_teen: 0,
  price_adult: 0,
  price_child: 0,
  sale_total: 0,
  sale_currency: "MXN",
  ota_commission: 0,
  balance_usd: 0,
  balance_mxn: 0,
  balance_eur: 0,
  provider_cost: 0,
  cost_currency: "MXN",
  booking_status: "confirmada",
};

function toForm(b: any): ExternalBookingInput {
  return {
    id: b.id,
    channel: b.channel,
    external_booking_id: b.external_booking_id,
    external_reference: b.external_reference,
    booked_at: b.booked_at,
    service_date: b.service_date,
    customer_first_name: b.customer_first_name,
    customer_last_name: b.customer_last_name,
    customer_email: b.customer_email,
    customer_phone: b.customer_phone,
    tour_title: b.tour_title,
    price_option: b.price_option,
    hotel: b.hotel,
    pickup_point: b.pickup_point,
    dropoff_point: b.dropoff_point,
    boarding: b.boarding,
    pax_adt: b.pax_adt ?? 0,
    pax_chd: b.pax_chd ?? 0,
    pax_inf: b.pax_inf ?? 0,
    pax_senior: b.pax_senior ?? 0,
    pax_student: b.pax_student ?? 0,
    pax_teen: b.pax_teen ?? 0,
    price_adult: Number(b.price_adult ?? 0),
    price_child: Number(b.price_child ?? 0),
    sale_total: Number(b.sale_total ?? 0),
    sale_currency: b.sale_currency ?? "MXN",
    ota_commission: Number(b.ota_commission ?? 0),
    balance_usd: Number(b.balance_usd ?? 0),
    balance_mxn: Number(b.balance_mxn ?? 0),
    balance_eur: Number(b.balance_eur ?? 0),
    provider_id: b.provider_id,
    provider_cost: Number(b.provider_cost ?? 0),
    cost_currency: b.cost_currency ?? "MXN",
    payment_due_date: b.payment_due_date,
    booking_status: b.booking_status ?? "confirmada",
    operation_status: b.operation_status,
    notes: b.notes,
    linked_booking_id: b.linked_booking_id,
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function BookingForm({
  value,
  providers,
  onClose,
  onSave,
  saving,
}: {
  value: ExternalBookingInput | null;
  providers: Array<{ id: string; name: string; currency?: string | null; default_commission_pct?: number | null }>;
  onClose: () => void;
  onSave: (v: ExternalBookingInput) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<ExternalBookingInput | null>(value);
  const key = value?.id ?? (value ? "new" : "closed");
  const [formKey, setFormKey] = useState(key);
  if (formKey !== key) {
    setFormKey(key);
    setForm(value);
  }
  const f = form;
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85dvh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{f?.id ? "Editar reserva externa" : "Nueva reserva externa"}</DialogTitle>
        </DialogHeader>
        {f ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Canal">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.channel}
                onChange={(e) => setForm({ ...f, channel: e.target.value })}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ID externo">
              <Input
                value={f.external_booking_id ?? ""}
                onChange={(e) => setForm({ ...f, external_booking_id: e.target.value })}
              />
            </Field>
            <Field label="Referencia / cupón">
              <Input
                value={f.external_reference ?? ""}
                onChange={(e) => setForm({ ...f, external_reference: e.target.value })}
              />
            </Field>
            <Field label="Fecha del servicio">
              <Input
                type="date"
                value={f.service_date ?? ""}
                onChange={(e) => setForm({ ...f, service_date: e.target.value || null })}
              />
            </Field>
            <Field label="Fecha de reserva">
              <Input
                type="date"
                value={f.booked_at ?? ""}
                onChange={(e) => setForm({ ...f, booked_at: e.target.value || null })}
              />
            </Field>
            <Field label="Tour">
              <Input
                value={f.tour_title ?? ""}
                onChange={(e) => setForm({ ...f, tour_title: e.target.value })}
              />
            </Field>
            <Field label="Nombre">
              <Input
                value={f.customer_first_name ?? ""}
                onChange={(e) => setForm({ ...f, customer_first_name: e.target.value })}
              />
            </Field>
            <Field label="Apellido">
              <Input
                value={f.customer_last_name ?? ""}
                onChange={(e) => setForm({ ...f, customer_last_name: e.target.value })}
              />
            </Field>
            <Field label="Email">
              <Input
                value={f.customer_email ?? ""}
                onChange={(e) => setForm({ ...f, customer_email: e.target.value })}
              />
            </Field>
            <Field label="Teléfono">
              <Input
                value={f.customer_phone ?? ""}
                onChange={(e) => setForm({ ...f, customer_phone: e.target.value })}
              />
            </Field>
            <Field label="Hotel">
              <Input value={f.hotel ?? ""} onChange={(e) => setForm({ ...f, hotel: e.target.value })} />
            </Field>
            <Field label="Abordaje / pickup">
              <Input
                value={f.boarding ?? ""}
                onChange={(e) => setForm({ ...f, boarding: e.target.value })}
              />
            </Field>
            <Field label="Adultos">
              <Input
                type="number"
                value={f.pax_adt}
                onChange={(e) => setForm({ ...f, pax_adt: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Niños">
              <Input
                type="number"
                value={f.pax_chd}
                onChange={(e) => setForm({ ...f, pax_chd: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Infantes">
              <Input
                type="number"
                value={f.pax_inf}
                onChange={(e) => setForm({ ...f, pax_inf: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Venta total">
              <Input
                type="number"
                value={f.sale_total}
                onChange={(e) => setForm({ ...f, sale_total: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Moneda de venta">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.sale_currency}
                onChange={(e) =>
                  setForm({ ...f, sale_currency: e.target.value as ExternalBookingInput["sale_currency"] })
                }
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Comisión OTA">
              <Input
                type="number"
                value={f.ota_commission}
                onChange={(e) => setForm({ ...f, ota_commission: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Proveedor asignado">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.provider_id ?? ""}
                onChange={(e) => {
                  const id = e.target.value || null;
                  const prov = providers.find((p) => p.id === id);
                  setForm({
                    ...f,
                    provider_id: id,
                    cost_currency: (prov?.currency ?? f.cost_currency) as ExternalBookingInput["cost_currency"],
                  });
                }}
              >
                <option value="">Sin asignar</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Costo del proveedor">
              <Input
                type="number"
                value={f.provider_cost}
                onChange={(e) => setForm({ ...f, provider_cost: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Moneda del costo">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.cost_currency}
                onChange={(e) =>
                  setForm({ ...f, cost_currency: e.target.value as ExternalBookingInput["cost_currency"] })
                }
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Fecha límite de pago">
              <Input
                type="date"
                value={f.payment_due_date ?? ""}
                onChange={(e) => setForm({ ...f, payment_due_date: e.target.value || null })}
              />
            </Field>
            <Field label="Estado de reserva">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.booking_status}
                onChange={(e) =>
                  setForm({ ...f, booking_status: e.target.value as ExternalBookingInput["booking_status"] })
                }
              >
                {BOOKING_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {BOOKING_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Estado operativo">
              <Input
                value={f.operation_status ?? ""}
                onChange={(e) => setForm({ ...f, operation_status: e.target.value })}
              />
            </Field>
            <div className="sm:col-span-3">
              <Field label="Notas">
                <Textarea
                  rows={3}
                  value={f.notes ?? ""}
                  onChange={(e) => setForm({ ...f, notes: e.target.value })}
                />
              </Field>
            </div>
            <div className="sm:col-span-3 flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button disabled={saving} onClick={() => onSave(f)}>
                Guardar
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

type ParsedFile = {
  headers: string[];
  rows: Array<Record<string, unknown>>;
  file: File;
  sheetName: string | null;
  sheetNames: string[];
};

type PreviewRow = {
  index: number;
  rowNumber: number;
  row: Record<string, unknown>;
  status: "nueva" | "modificada" | "duplicada" | "error";
  errors: string[];
  warnings: string[];
  matchedTourId: string | null;
  matchedTourTitle?: string | null;
  matchMethod?: string | null;
  changed: Array<{ field: string; before: string; after: string }>;
};

const STATUS_BADGE: Record<string, string> = {
  nueva: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  modificada: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  duplicada: "bg-muted text-muted-foreground",
  error: "bg-destructive/15 text-destructive",
};

const CHUNK = 200;

async function fileToBase64(f: File) {
  const buf = new Uint8Array(await f.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 8192) {
    bin += String.fromCharCode(...buf.subarray(i, i + 8192));
  }
  return btoa(bin);
}

function ImportDialog({
  open,
  providers,
  onClose,
  onDone,
}: {
  open: boolean;
  providers: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<ParsedFile | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [channel, setChannel] = useState("getyourguide");
  const [source, setSource] = useState<string>("getyourguide");
  const [providerId, setProviderId] = useState("");
  const [duplicateAction, setDuplicateAction] = useState<"ignore" | "update">("ignore");
  const [preview, setPreview] = useState<{
    summary: Record<string, number>;
    preview: PreviewRow[];
    errorRows: Array<{ rowNumber: number; errors: string[] }>;
  } | null>(null);
  const [result, setResult] = useState<{ inserted: number; updated: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [saveFile, setSaveFile] = useState(true);

  const templatesQ = useQuery({
    queryKey: ["import-templates"],
    queryFn: () => listImportTemplates(),
    enabled: open,
  });
  const templates = templatesQ.data?.templates ?? [];

  const mappedRows = useMemo(() => {
    if (!file) return [];
    return file.rows.map((r) => {
      const out: Record<string, unknown> = {};
      for (const [header, field] of Object.entries(mapping)) {
        if (!field) continue;
        out[field] = r[header];
      }
      return out;
    });
  }, [file, mapping]);

  function reset() {
    setPreview(null);
    setResult(null);
    setProgress(null);
  }

  async function parse(f: File, preferredSheet?: string | null) {
    const parsed = await parseTabularFile(f, preferredSheet ?? null);
    setFile({
      headers: parsed.headers,
      rows: parsed.rows,
      file: f,
      sheetName: parsed.sheetName,
      sheetNames: parsed.sheetNames,
    });
    // Si hay una plantilla del mismo canal, usar su mapeo; si no, autodetectar.
    const tpl = templates.find((t: any) => t.channel === channel);
    const tplMapping = (tpl?.mapping ?? null) as Record<string, string> | null;
    const auto = suggestMapping(parsed.headers, source);
    if (tplMapping) {
      const merged: Record<string, string> = { ...auto };
      for (const h of parsed.headers) if (tplMapping[h]) merged[h] = tplMapping[h]!;
      setMapping(merged);
      setTemplateId(tpl?.id ?? "");
    } else {
      setMapping(auto);
    }
    reset();
  }

  function autoDetect() {
    if (!file) return;
    setMapping(suggestMapping(file.headers, source));
    reset();
    toast.success("Mapeo detectado automáticamente");
  }

  async function runPreview() {
    if (!mappedRows.length) return;
    setBusy(true);
    reset();
    try {
      const res: any = await importExternalBookings({
        data: {
          channel,
          rows: mappedRows,
          commit: false,
          duplicateAction,
          defaultProviderId: providerId || null,
        },
      });
      setPreview({ summary: res.summary, preview: res.preview ?? [], errorRows: res.errorRows ?? [] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirmImport() {
    if (!file || !preview) return;
    setBusy(true);
    setResult(null);
    try {
      const fileBase64 = saveFile ? await fileToBase64(file.file) : null;
      const { batchId } = await startImportBatch({
        data: {
          fileName: file.file.name,
          channel,
          templateId: templateId || null,
          mapping,
          totalRows: mappedRows.length,
          fileBase64,
        },
      });

      let inserted = 0;
      let updated = 0;
      let duplicates = 0;
      setProgress({ done: 0, total: mappedRows.length });
      for (let i = 0; i < mappedRows.length; i += CHUNK) {
        const chunk = mappedRows.slice(i, i + CHUNK);
        const res: any = await importExternalBookings({
          data: {
            channel,
            rows: chunk,
            commit: true,
            duplicateAction,
            defaultProviderId: providerId || null,
            batchId,
          },
        });
        inserted += res.inserted ?? 0;
        updated += res.updated ?? 0;
        duplicates += res.summary?.duplicadas ?? 0;
        setProgress({ done: Math.min(i + CHUNK, mappedRows.length), total: mappedRows.length });
      }

      await completeImportBatch({
        data: {
          batchId,
          newRows: inserted,
          updatedRows: updated,
          duplicateRows: duplicates,
          errorRows: preview.summary["errores"] ?? 0,
          errors: preview.errorRows.slice(0, 500),
        },
      });

      setResult({ inserted, updated });
      toast.success(`Importación completada: ${inserted} nuevas, ${updated} actualizadas`);
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function persistTemplate() {
    if (!templateName.trim()) {
      toast.error("Ponle nombre a la plantilla");
      return;
    }
    try {
      await saveImportTemplate({ data: { name: templateName.trim(), channel, mapping } });
      toast.success("Plantilla guardada");
      setTemplateName("");
      await templatesQ.refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const blockingErrors = preview?.summary["errores"] ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📥 Importar Excel / CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="¿De dónde provienen los datos?">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={source}
                onChange={(e) => {
                  const id = e.target.value;
                  setSource(id);
                  const preset = IMPORT_SOURCES.find((x) => x.id === id);
                  if (preset) setChannel(preset.channel);
                  if (file) setMapping(suggestMapping(file.headers, id));
                  reset();
                }}
              >
                {IMPORT_SOURCES.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Canal de la venta">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={channel}
                onChange={(e) => {
                  setChannel(e.target.value);
                  reset();
                }}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Proveedor por defecto (opcional)">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
              >
                <option value="">Sin asignar</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Archivo (.xlsx, .xls, .csv)">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void parse(f);
                }}
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Plantilla de mapeo">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={templateId}
                onChange={(e) => {
                  const id = e.target.value;
                  setTemplateId(id);
                  const tpl = templates.find((t: any) => t.id === id);
                  if (tpl?.mapping) setMapping({ ...(tpl.mapping as Record<string, string>) });
                  reset();
                }}
              >
                <option value="">Autodetección</option>
                {templates.map((t: any) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {CHANNEL_LABEL[t.channel] ?? t.channel}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Guardar este mapeo como plantilla">
              <div className="flex gap-2">
                <Input
                  placeholder="Ej. Plantilla GetYourGuide"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
                <Button type="button" variant="outline" onClick={() => void persistTemplate()}>
                  Guardar
                </Button>
              </div>
            </Field>
            <Field label="Si la reserva ya existe">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={duplicateAction}
                onChange={(e) => {
                  setDuplicateAction(e.target.value as "ignore" | "update");
                  reset();
                }}
              >
                <option value="ignore">Ignorar (no duplicar)</option>
                <option value="update">Actualizar con los datos del archivo</option>
              </select>
            </Field>
          </div>

          {file ? (
            <>
              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold">
                    Mapear columnas ({file.rows.length} filas detectadas
                    {file.sheetName ? ` · hoja "${file.sheetName}"` : ""})
                  </h3>
                  <Button type="button" size="sm" variant="outline" onClick={autoDetect}>
                    Detectar automáticamente
                  </Button>
                </div>
                {file.sheetNames.length > 1 ? (
                  <div className="mb-2">
                    <Field label="Selecciona la hoja que deseas importar">
                      <select
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={file.sheetName ?? ""}
                        onChange={(e) => void parse(file.file, e.target.value)}
                      >
                        {file.sheetNames.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                ) : null}
                <div className="grid max-h-64 gap-2 overflow-y-auto rounded-lg border p-3 sm:grid-cols-2">
                  {file.headers.map((h) => (
                    <div key={h} className="flex items-center gap-2">
                      <span className="w-1/2 truncate text-xs text-muted-foreground" title={h}>
                        {h}
                      </span>
                      <select
                        className="h-9 w-1/2 rounded-md border bg-background px-2 text-xs"
                        value={mapping[h] ?? ""}
                        onChange={(e) => {
                          setMapping({ ...mapping, [h]: e.target.value });
                          reset();
                        }}
                      >
                        <option value="">Ignorar</option>
                        {IMPORT_FIELDS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={saveFile}
                  onChange={(e) => setSaveFile(e.target.checked)}
                />
                Guardar el archivo original para referencia administrativa
              </label>

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" disabled={busy} onClick={() => void runPreview()}>
                  Vista previa (sin guardar)
                </Button>
                <Button disabled={busy || !preview} onClick={() => void confirmImport()}>
                  Confirmar importación
                </Button>
                {progress ? (
                  <span className="text-xs text-muted-foreground">
                    Importando {progress.done} / {progress.total}
                  </span>
                ) : null}
              </div>

              {progress ? (
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
                  />
                </div>
              ) : null}

              {result ? (
                <div className="rounded-lg border bg-emerald-500/10 p-3 text-xs">
                  <p className="font-semibold">Importación completada</p>
                  <ul className="mt-1 grid gap-1 sm:grid-cols-4">
                    <li>✅ {result.inserted} nuevas</li>
                    <li>🔄 {result.updated} actualizadas</li>
                    <li>⚠️ {preview?.summary["duplicadas"] ?? 0} duplicadas</li>
                    <li>❌ {blockingErrors} con error</li>
                  </ul>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={onClose}>
                      Ver reservas importadas
                    </Button>
                    <Button size="sm" variant="ghost" asChild>
                      <Link to="/admin/import-history">Historial de importaciones</Link>
                    </Button>
                  </div>
                </div>
              ) : null}

              {preview ? (
                <div className="rounded-lg border bg-muted/40 p-3 text-xs">
                  <p className="font-semibold">
                    Vista previa · {file.file.name} · {preview.summary["total"] ?? 0} registros
                  </p>
                  <ul className="mt-1 grid gap-1 sm:grid-cols-5">
                    <li>Nuevas: {preview.summary["nuevas"] ?? 0}</li>
                    <li>Modificadas: {preview.summary["modificadas"] ?? 0}</li>
                    <li>Duplicadas: {preview.summary["duplicadas"] ?? 0}</li>
                    <li>Con error: {preview.summary["errores"] ?? 0}</li>
                    <li>Canceladas: {preview.summary["canceladas"] ?? 0}</li>
                    <li>Tours encontrados: {preview.summary["conTour"] ?? 0}</li>
                    <li>Tours sin encontrar: {preview.summary["sinTour"] ?? 0}</li>
                    <li>Pasajeros: {preview.summary["pasajeros"] ?? 0}</li>
                    <li>Ventas: {money(preview.summary["ventas"] ?? 0)}</li>
                    <li>Comisiones: {money(preview.summary["comisiones"] ?? 0)}</li>
                    <li>Neto: {money(preview.summary["neto"] ?? 0)}</li>
                  </ul>

                  <div className="mt-2 overflow-x-auto rounded border bg-background">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/60">
                        <tr>
                          <th className="px-2 py-1 text-left">Campo del sistema</th>
                          <th className="px-2 py-1 text-left">Columna detectada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappingReport(mapping).map((r) => (
                          <tr key={r.field} className="border-t">
                            <td className="px-2 py-1">{IMPORT_FIELD_LABEL[r.field] ?? r.label}</td>
                            <td className="px-2 py-1">
                              {r.header ?? <span className="text-muted-foreground">— sin mapear</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => reset()}>
                      ← Corregir mapeo
                    </Button>
                  </div>
                  {preview.errorRows.length ? (
                    <div className="mt-2 rounded border border-destructive/40 bg-destructive/10 p-2">
                      <p className="font-semibold text-destructive">🔴 Filas con error (no se importan)</p>
                      <ul className="mt-1 space-y-0.5">
                        {preview.errorRows.slice(0, 12).map((e) => (
                          <li key={e.rowNumber}>
                            La fila {e.rowNumber}: {e.errors.join(", ")}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 text-muted-foreground">
                        Corrige el Excel o el mapeo y vuelve a generar la vista previa.
                      </p>
                    </div>
                  ) : null}
                  <div className="mt-3 max-h-64 overflow-auto rounded border bg-background">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/60">
                        <tr>
                          <th className="px-2 py-1 text-left">Fila</th>
                          <th className="px-2 py-1 text-left">Estado</th>
                          <th className="px-2 py-1 text-left">Fecha</th>
                          <th className="px-2 py-1 text-left">Tour</th>
                          <th className="px-2 py-1 text-left">Catálogo</th>
                          <th className="px-2 py-1 text-left">Cliente</th>
                          <th className="px-2 py-1 text-right">PAX</th>
                          <th className="px-2 py-1 text-right">Venta</th>
                          <th className="px-2 py-1 text-right">Comisión</th>
                          <th className="px-2 py-1 text-right">Costo</th>
                          <th className="px-2 py-1 text-left">Detalle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.map((p) => (
                          <tr key={p.index} className="border-t align-top">
                            <td className="px-2 py-1">{p.rowNumber}</td>
                            <td className="px-2 py-1">
                              <span className={`rounded px-1.5 py-0.5 ${STATUS_BADGE[p.status]}`}>
                                {p.status}
                              </span>
                            </td>
                            <td className="px-2 py-1">{String(p.row["service_date"] ?? "")}</td>
                            <td className="px-2 py-1">
                              {String(p.row["tour_title"] ?? "")}
                            </td>
                            <td className="px-2 py-1">
                              {p.matchedTourTitle ? (
                                <>
                                  {p.matchedTourTitle}
                                  {p.matchMethod ? (
                                    <span className="text-muted-foreground"> ({p.matchMethod})</span>
                                  ) : null}
                                </>
                              ) : (
                                <span className="text-amber-600">sin coincidencia</span>
                              )}
                            </td>
                            <td className="px-2 py-1">
                              {String(p.row["customer_first_name"] ?? "")}{" "}
                              {String(p.row["customer_last_name"] ?? "")}
                            </td>
                            <td className="px-2 py-1 text-right">
                              {Number(p.row["pax_adt"] ?? 0) + Number(p.row["pax_chd"] ?? 0)}
                            </td>
                            <td className="px-2 py-1 text-right">{String(p.row["sale_total"] ?? "")}</td>
                            <td className="px-2 py-1 text-right">{String(p.row["ota_commission"] ?? "")}</td>
                            <td className="px-2 py-1 text-right">{String(p.row["provider_cost"] ?? "")}</td>
                            <td className="px-2 py-1 text-muted-foreground">
                              {p.errors.length ? p.errors.join(", ") : null}
                              {p.changed.length
                                ? p.changed
                                    .map((c) => `${c.field}: ${c.before || "—"} → ${c.after || "—"}`)
                                    .join(" · ")
                                : null}
                              {!p.errors.length && !p.changed.length && p.warnings.length
                                ? p.warnings.join(" · ")
                                : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Sube el reporte del canal (GetYourGuide, Civitatis, Viator, ventas externas u operaciones).
              Detectamos las columnas automáticamente, puedes ajustar el mapeo y guardarlo como plantilla.
              La vista previa marca nuevas, modificadas, duplicadas y filas con error sin guardar nada; al
              confirmar, cada fila se convierte en una reserva real en la base de datos y queda disponible en
              cuentas por pagar, pagos, conciliación y reportes.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
